import { useMemo } from 'react';
import { Skeleton } from '@art-of-hospitality-solutions-llc/ui-kit/components/skeleton';
import { ArrowDown, ArrowUp, ChevronsUpDown } from 'lucide-react';
import { cn } from '@Utils/cn';
import type { IndexSchema, SearchFieldType } from '@Hooks/useOmniSearch';
import { formatValue, getPath } from './utils';

export type SortState = { field: string; dir: 'asc' | 'desc' } | null;

function prettify(name: string): string {
  const leaf = name.split('.').pop() ?? name;
  return leaf
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
}

function formatCell(value: unknown, type?: SearchFieldType): string {
  if (value == null) return '—';
  if (type === 'date' && typeof value === 'number') {
    const d = new Date(value * 1000);
    return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
  }
  return formatValue(value);
}

export function OmniResultsTable({
  schema,
  hits,
  status,
  pageSize,
  sort,
  onSort,
  onRowClick,
}: {
  schema?: IndexSchema;
  hits: Record<string, any>[];
  status: 'idle' | 'pending' | 'error' | 'success';
  pageSize: number;
  sort?: SortState;
  onSort?: (field: string) => void;
  onRowClick: (hit: Record<string, any>) => void;
}) {
  const sortable = useMemo(() => new Set(schema?.sortable ?? []), [schema]);
  const typeByField = useMemo(() => {
    const m: Record<string, SearchFieldType> = {};
    (schema?.fields ?? []).forEach((f) => {
      m[f.name] = f.type;
    });
    return m;
  }, [schema]);

  // Columns: searchable display fields first, then any sortable fields not already shown (so
  // metrics/dates like createdDate are visible and clickable). Falls back to the first hit's
  // scalar keys when an index has no searchable config.
  const columns = useMemo<string[]>(() => {
    const searchable = (schema?.searchable ?? []).filter((s) => s && s !== '*');
    const base = searchable.length
      ? searchable
      : Object.keys(hits[0] ?? {})
          .filter((k) => {
            const v = (hits[0] as any)?.[k];
            return v == null || typeof v !== 'object' || Array.isArray(v);
          })
          .slice(0, 6);
    const extraSortable = (schema?.sortable ?? []).filter((s) => !base.includes(s));
    return [...base, ...extraSortable];
  }, [schema, hits]);

  const labelFor = (name: string) =>
    schema?.labels?.[name] ?? schema?.fields.find((f) => f.name === name)?.label ?? prettify(name);

  if (status === 'pending') {
    return (
      <div className="space-y-2">
        {Array.from({ length: Math.min(pageSize, 8) }).map((_, i) => (
          <Skeleton key={i} className="h-10 w-full" />
        ))}
      </div>
    );
  }

  if (status === 'error') {
    return <div className="p-6 text-sm text-red-600">Failed to load results.</div>;
  }

  if (!hits.length) {
    return <div className="p-6 text-sm text-zinc-500">No results.</div>;
  }

  return (
    <div className="overflow-x-auto rounded-lg border border-zinc-200">
      <table className="w-full text-left text-sm">
        <thead className="bg-zinc-50 text-xs uppercase text-zinc-500">
          <tr>
            {columns.map((col) => {
              const isSortable = sortable.has(col) && !!onSort;
              const active = sort?.field === col;
              return (
                <th key={col} className="whitespace-nowrap px-3 py-2 font-medium">
                  {isSortable ? (
                    <button
                      type="button"
                      onClick={() => onSort!(col)}
                      className={cn(
                        'flex items-center gap-1 hover:text-zinc-800',
                        active && 'text-zinc-900',
                      )}
                    >
                      {labelFor(col)}
                      {active ? (
                        sort!.dir === 'asc' ? (
                          <ArrowUp className="size-3" />
                        ) : (
                          <ArrowDown className="size-3" />
                        )
                      ) : (
                        <ChevronsUpDown className="size-3 opacity-40" />
                      )}
                    </button>
                  ) : (
                    labelFor(col)
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {hits.map((hit, i) => (
            <tr
              key={hit.id ?? i}
              className="cursor-pointer border-t border-zinc-100 hover:bg-zinc-50"
              onClick={() => onRowClick(hit)}
            >
              {columns.map((col) => {
                const text = formatCell(getPath(hit, col), typeByField[col]);
                return (
                  <td key={col} className="max-w-xs truncate px-3 py-2" title={text}>
                    {text}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
